// Fase 3 · Sincronización del TPV tras eventos del hub y huecos de conexión.
//
// - `requestSelectedCheckout(mesaId)`: refetch del checkout de la mesa
//   seleccionada, coalescido (una ráfaga de eventos → como mucho 2 GET) y
//   solo `data/getCheckout` (ni el plano entero ni getOrdersBySession: la
//   respuesta ya trae `session.pedidos` con los mismos campos).
// - `resyncTpvState(reason)`: resync único tras reconectar el hub o al
//   despertar la pestaña (mesas + checkout actual + presencia + último cobro).
//   Si llega otra petición mientras corre (o dentro del intervalo mínimo),
//   NO se descarta: se encola UNA repetición.
// - `runResumeRefresh(keys)`: ejecuta en paralelo el plan de
//   `realtime/resumePlan` (estado completo o lo que no viaja por la secuencia).
import axios from "./axios";
import { createCoalescingRunner } from "./tpvSyncCore";
import { markLocalCheckoutFetch } from "./localCheckoutFetchTracker";
import { invalidateHeldCheckouts, isMesaCheckoutBlocked } from "./mesaCheckoutLoader";
import { refreshPresence, refreshLastPayment, resumeLease } from "./mesaLeaseManager";
import { getMesas } from "src/redux/slices/mesas";
import { setActiveSession } from "src/redux/slices/active";
import { syncCartCheckoutComplete, fetchComensales, getProducts } from "src/redux/slices/product";
import { getSessionSuccess } from "src/redux/slices/session";
import { getOrdersSuccess, getOnlyOrders } from "src/redux/slices/order";
import { getCategories } from "src/redux/slices/categories";
import { fetchUpcomingBookings, fetchBookingAlerts } from "src/redux/slices/bookings";
import { getNotifications } from "src/redux/slices/notifications";
import { getActiveAddOns } from "src/redux/slices/addOns";
import { getUser } from "src/redux/slices/user";
import { upsertTx } from "src/redux/slices/cashdrawer";
import { getTicketsByDisplay } from "src/redux/slices/kitchenTickets";
import { KEYS } from "src/realtime/resumePlan";

let storeRef = null;

/** Lo llama el hook del hub de mesas para dar acceso a dispatch/getState. */
export function bindTpvResyncStore(dispatch, getState) {
  storeRef = { dispatch, getState };
}

async function fetchCheckout(mesaId) {
  if (!storeRef || !mesaId) return;
  const { dispatch, getState } = storeRef;
  const userId = getState()?.authJwt?.user?.id;
  if (!userId) return;

  // Mesa cobrándose en otro dispositivo cuya cuenta no se ha pintado (aviso
  // «¿Forzar?»): un evento en tiempo real no debe pintarla por detrás.
  const blocked = () =>
    isMesaCheckoutBlocked(mesaId) ||
    String(getState()?.mesaLeases?.chargingPrompt?.mesaId) === String(mesaId);
  if (blocked()) return;
  markLocalCheckoutFetch(mesaId);
  // Lo dispara un evento: no vale unirse a un getCheckout que salió antes.
  const response = await axios.get(`data/getCheckout/${mesaId}/${userId}`, { skipDedupe: true });
  // Si mientras tanto el camarero cambió de mesa, no pisamos su estado.
  if (String(getState()?.mesas?.selectedId) !== String(mesaId)) return;
  if (blocked()) return;
  const session = response?.data?.session;
  if (!session) return;

  dispatch(setActiveSession(session.id));
  if (Array.isArray(session.pedidos)) dispatch(getOrdersSuccess(session.pedidos));
  dispatch(syncCartCheckoutComplete(response.data.checkout));
  dispatch(getSessionSuccess(session));
}

const checkoutRunner = createCoalescingRunner(fetchCheckout);

export function requestSelectedCheckout(mesaId) {
  return checkoutRunner.request(mesaId);
}

const RESYNC_MIN_INTERVAL_MS = 3000;
// Misma configuración que la lista de pedidos de los handlers del hub
// (tpvEventHandlers.ONLY_ORDERS_CONFIG); copiada para no crear un ciclo.
const ONLY_ORDERS_CONFIG = { OnlyOrders: false, Last24Hours: true };
let lastResyncAt = 0;
let resyncInFlight = null;
let resyncQueued = null; // promesa de la repetición encolada
let resyncQueuedReason = null;

const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

function selectedMesaId() {
  const selectedId = storeRef?.getState()?.mesas?.selectedId;
  return selectedId == null || selectedId === -1 ? null : selectedId;
}

function runCoreResync(reason) {
  const { dispatch } = storeRef;
  lastResyncAt = Date.now();
  if (process.env.NODE_ENV !== "production") console.debug(`[tpvResync] ${reason}`);
  return Promise.allSettled([
    dispatch(getMesas({ fresh: true })),
    (() => {
      const id = selectedMesaId();
      return id == null ? Promise.resolve() : requestSelectedCheckout(id);
    })(),
    refreshPresence(),
    refreshLastPayment(),
  ]);
}

/**
 * Resync tras un hueco (reconexión del hub, pestaña que despierta). Varios
 * hubs reconectan a la vez: se agrupan en uno. Una petición que llega con
 * uno en curso, o antes del intervalo mínimo, deja UNA repetición encolada
 * (antes se descartaba y podía quedarse sin aplicar un cambio).
 */
export function resyncTpvState(reason = "resync") {
  // Tras reconectar/despertar pudo cambiar cualquier mesa sin que llegara su
  // evento: ninguna cuenta guardada (mesa cobrándose) vale ya.
  invalidateHeldCheckouts();
  if (!storeRef) return Promise.resolve();
  if (isOffline()) return Promise.resolve();
  if (resyncInFlight || Date.now() - lastResyncAt < RESYNC_MIN_INTERVAL_MS) {
    resyncQueuedReason = reason;
    if (resyncQueued) return resyncQueued;
    resyncQueued = (async () => {
      try {
        if (resyncInFlight) await resyncInFlight;
      } catch {
        /* ignore */
      }
      const left = RESYNC_MIN_INTERVAL_MS - (Date.now() - lastResyncAt);
      if (left > 0) await wait(left);
      const r = resyncQueuedReason;
      resyncQueued = null;
      resyncQueuedReason = null;
      if (!storeRef || isOffline()) return undefined;
      return startResync(`${r} (queued)`);
    })();
    return resyncQueued;
  }
  return startResync(reason);
}

function startResync(reason) {
  resyncInFlight = runCoreResync(reason).finally(() => {
    resyncInFlight = null;
  });
  return resyncInFlight;
}

async function refreshCheckoutAndComensales() {
  const id = selectedMesaId();
  if (id == null) return;
  await requestSelectedCheckout(id);
  const sid = storeRef.getState()?.session?.session?.id;
  if (sid) await storeRef.dispatch(fetchComensales(sid));
}

async function refreshCashDrawer() {
  const pending = storeRef.getState()?.cashdrawer?.pendingTx || {};
  const ids = Object.keys(pending);
  if (!ids.length) return;
  const res = await axios.get("cashdrawer/transactions", { params: { take: 50 } });
  const list = Array.isArray(res?.data) ? res.data : [];
  list.filter((t) => t?.id && pending[t.id]).forEach((t) => storeRef.dispatch(upsertTx(t)));
}

/**
 * Ejecuta en paralelo las piezas de `planResumeRefresh`. Devuelve
 * `{ ok, failed: [keys] }`; nunca lanza. Los thunks que tragan el error
 * (la mayoría) cuentan como ok: el chip de estado se basa en la red y en
 * el catch-up del tiempo real, que sí fallan de forma visible.
 */
export async function runResumeRefresh(keys = [], { restauranteId } = {}) {
  invalidateHeldCheckouts();
  if (!storeRef) return { ok: true, failed: [] };
  const { dispatch, getState } = storeRef;
  const st = getState();
  const rid = restauranteId || st?.authJwt?.user?.id;
  const jobs = {
    [KEYS.mesas]: () => dispatch(getMesas({ fresh: true })),
    [KEYS.checkout]: () => refreshCheckoutAndComensales(),
    [KEYS.comensales]: () => Promise.resolve(), // va con el checkout (necesita la sesión fresca)
    [KEYS.presence]: () => refreshPresence(),
    [KEYS.lease]: () => resumeLease(),
    [KEYS.lastPayment]: () => refreshLastPayment(),
    [KEYS.onlyOrders]: () => dispatch(getOnlyOrders(ONLY_ORDERS_CONFIG, { force: true })),
    [KEYS.bookings]: () =>
      Promise.all([
        dispatch(fetchUpcomingBookings({ limit: 30, force: true })),
        dispatch(fetchBookingAlerts({ force: true })),
      ]),
    [KEYS.notifications]: () => dispatch(getNotifications()),
    [KEYS.addOns]: () => dispatch(getActiveAddOns({ force: true })),
    [KEYS.user]: () => (rid ? dispatch(getUser(rid)) : Promise.resolve()),
    [KEYS.cashDrawer]: () => refreshCashDrawer(),
    [KEYS.catalog]: () =>
      Promise.all([dispatch(getProducts(undefined, { force: true })), dispatch(getCategories(undefined, { force: true }))]),
    [KEYS.kds]: () => {
      const displayId = st?.kitchenTickets?.currentWorkingDisplay;
      return displayId ? dispatch(getTicketsByDisplay(displayId)) : Promise.resolve();
    },
  };
  // El plano completo también cuenta como resync "core" (evita que una
  // reconexión legacy inmediatamente después lo repita).
  if (keys.includes(KEYS.mesas)) lastResyncAt = Date.now();
  const list = keys.filter((k) => jobs[k]);
  const results = await Promise.allSettled(list.map((k) => Promise.resolve().then(jobs[k])));
  const failed = list.filter((k, i) => results[i].status === "rejected");
  return { ok: failed.length === 0, failed };
}

/** Solo para tests. */
export function __resetTpvResyncForTests() {
  lastResyncAt = 0;
  resyncInFlight = null;
  resyncQueued = null;
  resyncQueuedReason = null;
  storeRef = null;
}
